import moment from 'moment';

import { DataGridColSettings } from '@/components/DataGrid/utils';
import { DATE_TIME_FORMAT_WITH_SECONDS, TIME_FORMAT } from '@/config';
import { IKaseNewForm } from '@/models';

export const kaseDataGridColumns: DataGridColSettings[] = [
  {
    field: 'priority',
    headerName: 'Priority',
    width: 70,
    visible: true,
  },
  {
    field: 'number',
    headerName: 'Case ID',
    width: 150,
    visible: true,
  },
  {
    field: 'createdAt',
    headerName: 'Date created',
    width: 140,
    visible: true,
  },
  {
    field: 'approvedInfo',
    headerName: 'Date approved',
    width: 140,
    visible: true,
  },
  {
    field: 'updatedAt',
    headerName: 'Last edited',
    width: 140,
    visible: true,
  },
  {
    field: 'masterNameCount',
    headerName: 'Names(count)',
    width: 130,
    visible: true,
  },
  {
    field: 'address',
    headerName: 'Location',
    visible: true,
  },
  {
    field: 'incidentCodes',
    headerName: 'Incident Codes',
    visible: true,
  },
  {
    field: 'createdBy',
    headerName: 'Created by',
    visible: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 160,
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 80,
    visible: true,
  },
];

export const filterMenuFormFields = [
  {
    name: 'kasesId',
    type: 'autocomplete',
    defaultValue: '0',
    items: [
      {
        label: 'Case ID ',
        value: '0',
      },
      {
        label: 'Case ID 1 ',
        value: 'Case ID1 first options',
      },
      {
        label: 'Case ID 2',
        value: 'Case ID 2',
      },
      {
        label: 'Case ID 3 ',
        value: 'Case ID 3',
      },
    ],
  },
  {
    name: 'contains',
    type: 'autocomplete',
    defaultValue: '0',
    items: [
      {
        label: 'Contains',
        value: '0',
      },
      {
        label: 'item1',
        value: 'item1',
      },
      {
        label: 'item2',
        value: 'item2',
      },
      {
        label: 'item3',
        value: 'item3',
      },
    ],
  },
  {
    name: 'custom',
    type: 'text',
    label: 'Enter value',
    placeholder: 'Enter Value',
  },
];

export const setInvolvementFormFields = [
  {
    name: 'behavior',
    type: 'autocomplete',
    label: 'Behavior',
    items: [
      {
        label: 'behavior1',
        value: 'Behavoir Option 1',
      },
      {
        label: 'behavior2',
        value: 'Behavoir Option 2',
      },
      {
        label: 'behavior3',
        value: 'Behavoir Option 3',
      },
    ],
  },
  {
    name: 'involvement',
    type: 'autocomplete',
    label: 'Involvement',
    items: [
      {
        label: 'Involvement1',
        value: 'Involvement Option 1',
      },
      {
        label: 'Involvement2',
        value: 'Involvement Option 2',
      },
      {
        label: 'Involvement3',
        value: 'Involvement Option 3',
      },
    ],
  },
];

export const DateAndTimeOptions = [
  { label: 'Not reported', value: 'NOT_REPORTED' },
  { label: 'Unknown', value: 'UNKNOWN' },
];

export const KaseFormFields = [
  {
    name: 'type',
    label: 'Case type',
    type: 'autocomplete',
    rules: {
      required: 'This field is required.',
    },
    grid: { md: 12 },
  },
  {
    name: 'primaryOfficer',
    label: 'Primary officer',
    type: 'autocomplete',
    rules: {
      required: 'This field is required.',
    },
    grid: { md: 6 },
  },
  {
    name: 'primaryInvestigator',
    label: 'Primary investigator',
    type: 'autocomplete',
    grid: { md: 6 },
  },
  {
    name: 'address',
    label: 'Address',
    type: 'address',
    rules: {
      required: 'This field is required.',
      validate: (value: IKaseNewForm['address']) =>
        !value || value._id
          ? true
          : 'Please wait for the address to finish loading.',
    },
    grid: { md: 12 },
  },
  {
    name: 'locationCode',
    label: 'Location code of Incident',
    type: 'autocomplete',
    freeSolo: true,
    // multiple: false,
    items: 'ctvCodes',
    grid: { md: 6 },
  },
  {
    name: 'sdpIdentifier',
    label: 'Station/Division/Precinct Identifier',
    grid: { md: 6 },
    rules: {
      pattern: {
        value: /^[a-zA-Z0-9]+$/,
        message: 'Only alphanumeric values allowed.',
      },
      maxLength: {
        value: 6,
        message:
          'Station/Division/Precinct Identifier should be less than 6 characters long.',
      },
    },
    clearable: true,
  },
  {
    name: 'incidentStartedAt',
    type: 'datetimeselect',
    grid: { md: 10 },
    date: {
      name: 'date',
      label: 'Incident start date',
      required: true,
      clearable: true,
      rules: {
        validate: (
          value: string,
          { incidentReportedAt, incidentStartedAtOption }: IKaseNewForm,
        ) => {
          if (!value) {
            if (incidentStartedAtOption) return true;
            return 'This field is required.';
          }

          if (!String(value).includes(':') && !incidentStartedAtOption) {
            return 'This field is required.';
          }

          if (!incidentReportedAt) return true;

          return new Date(value) <= new Date(incidentReportedAt)
            ? true
            : 'Start date must be the same or earlier than Report date.';
        },
      },
    },
    time: {
      name: 'time',
      label: 'Incident start time',
      timeSteps: { hours: 1, minutes: 1 },
      maxDateTime: moment().minute(moment().minute()),
      views: ['hours', 'minutes'],
      format: TIME_FORMAT,
      rules: { required: 'This field is required.' },
    },
    select: {
      name: 'incidentStartedAtOption',
      label: "Don't know the date and time?",
      items: DateAndTimeOptions,
    },
    format: DATE_TIME_FORMAT_WITH_SECONDS,
  },
  {
    name: 'incidentEndedAt',
    type: 'datetimeselect',
    grid: { md: 10 },
    date: {
      name: 'date',
      label: 'Incident end date',
      clearable: true,
      rules: {
        validate: (value: string, { incidentStartedAt }: IKaseNewForm) => {
          if (!value) return true;
          if (!incidentStartedAt) return true;

          return new Date(value) >= new Date(incidentStartedAt)
            ? true
            : 'End date must be the same or later than Start date.';
        },
      },
    },
    time: {
      name: 'time',
      label: 'Incident end time',
      timeSteps: { hours: 1, minutes: 1 },
      maxDateTime: moment().minute(moment().minute()),
      views: ['hours', 'minutes'],
      format: TIME_FORMAT,
    },
    format: DATE_TIME_FORMAT_WITH_SECONDS,
  },
  {
    name: 'incidentReportedAt',
    label: 'Incident Report date and time',
    type: 'datetimeselect',
    grid: { md: 10 },
    date: {
      name: 'date',
      label: 'Incident report date',
      required: true,
      rules: {
        validate: (
          value: string,
          { incidentStartedAt, incidentReportedAtTimeOption }: IKaseNewForm,
        ) => {
          if (!value) {
            return 'This field is required.';
          }

          if (!String(value).includes(':') && !incidentReportedAtTimeOption) {
            return 'This field is required.';
          }

          if (moment(value).isAfter(moment())) {
            return 'Report date cannot be in the future.';
          }

          if (!incidentStartedAt) return true;

          if (incidentReportedAtTimeOption) {
            const reportDate = moment(value).startOf('day');
            const incidentDate = moment(incidentStartedAt).startOf('day');
            return reportDate.isSameOrAfter(incidentDate)
              ? true
              : 'Report date must be the same or later than incident date.';
          }

          return new Date(value) >= new Date(incidentStartedAt)
            ? true
            : 'Report date must be the same or later than incident date.';
        },
      },
      clearable: true,
    },
    time: {
      name: 'time',
      label: 'Incident report time',
      timeSteps: { hours: 1, minutes: 1 },
      maxDateTime: moment().minute(moment().minute()),
      views: ['hours', 'minutes'],
      format: TIME_FORMAT,
      rules: { required: 'This field is required.' },
    },
    select: {
      name: 'incidentReportedAtTimeOption',
      label: "Don't know the time?",
      items: DateAndTimeOptions,
      keepDate: true,
    },
    format: DATE_TIME_FORMAT_WITH_SECONDS,
  },
];
